import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { Observable, Subscription, throwError, take, filter, switchMap, catchError, withLatestFrom } from 'rxjs';

import { AppState, ConfigService, mapError, mapErrorForMonitoring, MonitoringLogger } from '@fcom/core';
import { Profile, ProfileType } from '@fcom/core-api/login/interfaces/login.interface';
import {
  AccountSecurityActionName,
  AccountSecurityChangeEmailFlowStep,
  AccountSecurityError,
  AccountSecurityFlowType,
  MfaProvider,
} from '@fcom/core-api/login/interfaces/account-security.interface';
import {
  profile,
  loginToken,
  accountSecurityLastStandaloneActionIsLoading,
  accountSecurityFlowLastActionIsLoading,
  accountSecurityFlowData,
  accountSecurityFlowStep,
  profileType,
} from '@fcom/core/selectors/login.selector';
import { finShare, snapshot } from '@fcom/rx';
import * as LoginActions from '@fcom/core/actions/login.actions';

import {
  EnableGAuthValidatedResponse,
  GetGAuthStatusResponse,
  isValidate2faEmailVerificationCodeErrorResponseWithCode,
  Validate2faEmailVerificationCodeErrorCode,
  Validate2faEmailVerificationCodeErrorResponse,
  Validate2faEmailVerificationCodeRequest,
  Validate2faEmailVerificationCodeResponse,
  ValidateGAuthResponse,
} from '../interfaces/account-security.interface';

const STATUS_PATH = 'getgauth';
const ENABLE_VALIDATED_PATH = 'enablegauthvalidated';
const VALIDATE_PATH = 'validategauth';
const DISABLE_PATH = 'disablegauth';

@Injectable({ providedIn: 'root' })
export class AccountSecurityService implements OnDestroy {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private store$ = inject<Store<AppState>>(Store);
  private monitoringLogger = inject(MonitoringLogger);

  private requestSubscription?: Subscription = undefined;

  private profile$: Observable<Profile> = this.store$.pipe(profile(), finShare());

  constructor() {
    this.store$
      .pipe(
        profileType(),
        filter((type) => type == ProfileType.FPLUS),
        take(1)
      )
      .subscribe({
        next: () => {
          this.getStatus();
        },
      });
  }

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
  }

  getStatus(): void {
    const isLoading = snapshot(this.store$.pipe(accountSecurityLastStandaloneActionIsLoading()));

    if (isLoading) {
      return;
    }

    this.store$.dispatch(
      LoginActions.setAccountSecurityStandaloneAction({
        standaloneAction: {
          name: AccountSecurityActionName.GetMfaStatus,
          isLoading: true,
        },
      })
    );

    this.gAuthRequest<GetGAuthStatusResponse>(STATUS_PATH).subscribe({
      next: (response) => {
        const appAuthenticator = response.secretKey && response.validated !== false;

        let mfaProvider: MfaProvider;

        if (response.emailAuthenticator) {
          mfaProvider = MfaProvider.EmailAuth;
        } else if (appAuthenticator) {
          mfaProvider = MfaProvider.MfaGauth;
        } else if (response.smsAuthenticator) {
          mfaProvider = MfaProvider.SmsAuth;
        } else {
          mfaProvider = MfaProvider.None;
        }

        this.store$.dispatch(
          LoginActions.setAccountSecurityStandaloneAction({
            standaloneAction: {
              name: AccountSecurityActionName.GetMfaStatus,
              isLoading: false,
              successData: { mfaProvider },
            },
          })
        );
      },
      error: (error: unknown) => {
        this.store$.dispatch(
          LoginActions.setAccountSecurityStandaloneAction({
            standaloneAction: {
              name: AccountSecurityActionName.GetMfaStatus,
              isLoading: false,
              error: AccountSecurityError.Unknown,
            },
          })
        );

        this.monitoringLogger.error('Error getting MFA status', {
          error: mapErrorForMonitoring(error),
        });
      },
    });
  }

  disableGAuth(): void {
    const isLoading = snapshot(this.store$.pipe(accountSecurityLastStandaloneActionIsLoading()));

    if (isLoading) {
      return;
    }

    this.store$.dispatch(
      LoginActions.setAccountSecurityStandaloneAction({
        standaloneAction: { name: AccountSecurityActionName.DisableGAuth, isLoading: true },
      })
    );

    this.gAuthRequest<unknown>(DISABLE_PATH).subscribe({
      next: () => {
        this.store$.dispatch(
          LoginActions.setAccountSecurityStandaloneAction({
            standaloneAction: {
              name: AccountSecurityActionName.DisableGAuth,
              isLoading: false,
            },
          })
        );
      },
      error: (error: unknown) => {
        this.store$.dispatch(
          LoginActions.setAccountSecurityStandaloneAction({
            standaloneAction: {
              name: AccountSecurityActionName.DisableGAuth,
              isLoading: false,
              error: AccountSecurityError.Unknown,
            },
          })
        );

        this.monitoringLogger.error('Error disabling GAuth', {
          error: mapErrorForMonitoring(error),
        });
      },
    });
  }

  enableGAuthValidated(): void {
    const isLoading = snapshot(
      this.store$.pipe(accountSecurityFlowLastActionIsLoading(AccountSecurityFlowType.EnableAppMfa))
    );

    if (isLoading) {
      return;
    }

    this.store$.dispatch(
      LoginActions.setAccountSecurityFlowAction({
        flowType: AccountSecurityFlowType.EnableAppMfa,
        flowAction: {
          name: AccountSecurityActionName.EnableGAuthValidated,
          isLoading: true,
        },
      })
    );

    this.requestSubscription = this.gAuthRequest<EnableGAuthValidatedResponse>(ENABLE_VALIDATED_PATH).subscribe({
      next: (response) => {
        this.store$.dispatch(
          LoginActions.setAccountSecurityFlowAction({
            flowType: AccountSecurityFlowType.EnableAppMfa,
            flowAction: {
              name: AccountSecurityActionName.EnableGAuthValidated,
              isLoading: false,
              successData: {
                secretKey: response.secretKey,
                qrCodeVerificationUrl: this.getQRCodeImageUrl(response.secretKey),
              },
            },
          })
        );
      },
      error: (error: unknown) => {
        this.store$.dispatch(
          LoginActions.setAccountSecurityFlowAction({
            flowType: AccountSecurityFlowType.EnableAppMfa,
            flowAction: {
              name: AccountSecurityActionName.EnableGAuthValidated,
              isLoading: false,
              error: AccountSecurityError.Unknown,
            },
          })
        );

        this.monitoringLogger.error('Error in the process of enabling app MFA - enabling GAuth Validated', {
          error: mapErrorForMonitoring(error),
        });
      },
    });
  }

  validateGAuth(): void {
    const isLoading = snapshot(
      this.store$.pipe(accountSecurityFlowLastActionIsLoading(AccountSecurityFlowType.EnableAppMfa))
    );

    if (isLoading) {
      return;
    }

    const flowData = snapshot(this.store$.pipe(accountSecurityFlowData(AccountSecurityFlowType.EnableAppMfa)));

    if (!flowData.form.valid) {
      this.store$.dispatch(
        LoginActions.setAccountSecurityFlowAction({
          flowType: AccountSecurityFlowType.EnableAppMfa,
          flowAction: {
            name: undefined,
            isLoading: false,
          },
        })
      );
      return;
    }

    this.store$.dispatch(
      LoginActions.setAccountSecurityFlowAction({
        flowType: AccountSecurityFlowType.EnableAppMfa,
        flowAction: { name: AccountSecurityActionName.ValidateGAuth, isLoading: true },
      })
    );

    const { verificationCode } = flowData.form;

    this.requestSubscription = this.gAuthRequest<ValidateGAuthResponse>(
      `${VALIDATE_PATH}/${verificationCode}`
    ).subscribe({
      next: (response) => {
        if (response.valid) {
          this.store$.dispatch(
            LoginActions.setAccountSecurityFlowAction({
              flowType: AccountSecurityFlowType.EnableAppMfa,
              flowAction: {
                name: AccountSecurityActionName.ValidateGAuth,
                isLoading: false,
              },
            })
          );
        } else {
          this.store$.dispatch(
            LoginActions.setAccountSecurityFlowAction({
              flowType: AccountSecurityFlowType.EnableAppMfa,
              flowAction: {
                name: AccountSecurityActionName.ValidateGAuth,
                isLoading: false,
                error: AccountSecurityError.Unknown,
              },
            })
          );
        }
      },
      error: (error: unknown) => {
        this.store$.dispatch(
          LoginActions.setAccountSecurityFlowAction({
            flowType: AccountSecurityFlowType.EnableAppMfa,
            flowAction: {
              name: AccountSecurityActionName.ValidateGAuth,
              isLoading: false,
              error: AccountSecurityError.Unknown,
            },
          })
        );

        this.monitoringLogger.error(
          'Error in the process of enabling app MFA - validating the 2fa app verification code has failed',
          {
            error: mapErrorForMonitoring(error),
          }
        );
      },
    });
  }

  enableEmailMfa(flowType: AccountSecurityFlowType.EnableEmailMfa | AccountSecurityFlowType.SoftEnforceEmailMfa): void {
    const isLoading = snapshot(this.store$.pipe(accountSecurityFlowLastActionIsLoading(flowType)));

    if (isLoading) {
      return;
    }

    this.store$.dispatch(
      LoginActions.setAccountSecurityFlowAction({
        flowType,
        flowAction: { name: AccountSecurityActionName.EnableEmailMfa, isLoading: true },
      })
    );

    const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyProfileMfaEmailEnable}`;
    const httpOptions = {
      headers: this.getLoyaltyServicesHttpHeaders(),
    };

    this.requestSubscription = this.http.post<undefined>(apiUrl, undefined, httpOptions).subscribe({
      next: () => {
        this.store$.dispatch(
          LoginActions.setAccountSecurityFlowAction({
            flowType,
            flowAction: {
              name: AccountSecurityActionName.EnableEmailMfa,
              isLoading: false,
            },
          })
        );
      },
      error: (error: unknown) => {
        this.store$.dispatch(
          LoginActions.setAccountSecurityFlowAction({
            flowType,
            flowAction: {
              name: AccountSecurityActionName.EnableEmailMfa,
              isLoading: false,
              error: AccountSecurityError.Unknown,
            },
          })
        );

        this.monitoringLogger.error('Error enabling email MFA', {
          error: mapErrorForMonitoring(error),
        });
      },
    });
  }

  send2faEmailVerificationCode(
    flowType:
      | AccountSecurityFlowType.EnableEmailMfa
      | AccountSecurityFlowType.SoftEnforceEmailMfa
      | AccountSecurityFlowType.VerifyEmail
  ): void {
    const isLoading = snapshot(this.store$.pipe(accountSecurityFlowLastActionIsLoading(flowType)));

    if (isLoading) {
      return;
    }

    this.store$.dispatch(
      LoginActions.setAccountSecurityFlowAction({
        flowType,
        flowAction: {
          name: AccountSecurityActionName.Send2faEmailVerificationCode,
          isLoading: true,
        },
      })
    );

    const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyProfileEmailVerifyUrl}`;
    const httpOptions = {
      headers: this.getLoyaltyServicesHttpHeaders(),
    };

    this.requestSubscription = this.http.post<undefined>(apiUrl, null, httpOptions).subscribe({
      next: () => {
        this.store$.dispatch(
          LoginActions.setAccountSecurityFlowAction({
            flowType: flowType,
            flowAction: {
              name: AccountSecurityActionName.Send2faEmailVerificationCode,
              isLoading: false,
            },
          })
        );
      },
      error: (error: unknown) => {
        this.store$.dispatch(
          LoginActions.setAccountSecurityFlowAction({
            flowType,
            flowAction: {
              name: AccountSecurityActionName.Send2faEmailVerificationCode,
              isLoading: false,
              error: AccountSecurityError.Send2faEmailVerificationCode,
            },
          })
        );

        this.monitoringLogger.error(
          'Error in the process of enabling email MFA - sending 2fa email verification code has failed',
          {
            error: mapErrorForMonitoring(error),
          }
        );
      },
    });
  }

  initiateEmailChange(): void {
    const isLoading = snapshot(
      this.store$.pipe(accountSecurityFlowLastActionIsLoading(AccountSecurityFlowType.ChangeEmail))
    );

    if (isLoading) {
      return;
    }
    const flowStep = snapshot(this.store$.pipe(accountSecurityFlowStep(AccountSecurityFlowType.ChangeEmail)));
    const flowData = snapshot(this.store$.pipe(accountSecurityFlowData(AccountSecurityFlowType.ChangeEmail)));

    if (flowStep === AccountSecurityChangeEmailFlowStep.EnterNewEmail && !flowData.form.valid) {
      this.store$.dispatch(
        LoginActions.setAccountSecurityFlowAction({
          flowType: AccountSecurityFlowType.ChangeEmail,
          flowAction: {
            name: undefined,
            isLoading: false,
          },
        })
      );
      return;
    }

    this.store$.dispatch(
      LoginActions.setAccountSecurityFlowAction({
        flowType: AccountSecurityFlowType.ChangeEmail,
        flowAction: {
          name: AccountSecurityActionName.InitiateEmailChange,
          isLoading: true,
        },
      })
    );

    const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyProfileEmailChangeUrl}`;
    const httpOptions = {
      headers: this.getLoyaltyServicesHttpHeaders(),
    };

    const { newEmail } = flowData.form;

    this.requestSubscription = this.http.patch<undefined>(apiUrl, { newEmail }, httpOptions).subscribe({
      next: () => {
        this.store$.dispatch(
          LoginActions.setAccountSecurityFlowAction({
            flowType: AccountSecurityFlowType.ChangeEmail,
            flowAction: {
              name: AccountSecurityActionName.InitiateEmailChange,
              isLoading: false,
            },
          })
        );
      },
      error: (error: unknown) => {
        this.monitoringLogger.error(
          'Error in the process of initiating email change - sending email change verification code has failed',
          {
            error: mapErrorForMonitoring(error),
          }
        );

        const status = (error as HttpErrorResponse)?.status;
        let accountSecurityError: AccountSecurityError;

        switch (status) {
          case 400:
            accountSecurityError = AccountSecurityError.InitiateEmailChangeInvalidEmail;
            break;
          case 409:
            accountSecurityError = AccountSecurityError.InitiateEmailChangeSameEmail;
            break;
          case 503:
          default:
            accountSecurityError = AccountSecurityError.Unknown;
        }

        this.store$.dispatch(
          LoginActions.setAccountSecurityFlowAction({
            flowType: AccountSecurityFlowType.ChangeEmail,
            flowAction: {
              name: AccountSecurityActionName.InitiateEmailChange,
              isLoading: false,
              error: accountSecurityError,
            },
          })
        );
      },
    });
  }

  validate2faEmailVerificationCode(
    flowType:
      | AccountSecurityFlowType.EnableEmailMfa
      | AccountSecurityFlowType.SoftEnforceEmailMfa
      | AccountSecurityFlowType.VerifyEmail
      | AccountSecurityFlowType.ChangeEmail
  ): void {
    const isLoading = snapshot(this.store$.pipe(accountSecurityFlowLastActionIsLoading(flowType)));
    if (isLoading) {
      return;
    }

    this.store$.dispatch(
      LoginActions.setAccountSecurityFlowAction({
        flowType,
        flowAction: {
          name: AccountSecurityActionName.Validate2faEmailVerificationCode,
          isLoading: true,
        },
      })
    );

    const flowData = snapshot(this.store$.pipe(accountSecurityFlowData(flowType)));

    if (!flowData.form.valid) {
      this.store$.dispatch(
        LoginActions.setAccountSecurityFlowAction({
          flowType,
          flowAction: {
            name: undefined,
            isLoading: false,
          },
        })
      );
      return;
    }

    const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyProfileEmailConfirmUrl}`;
    const httpOptions = {
      headers: this.getLoyaltyServicesHttpHeaders(),
    };

    this.requestSubscription = this.http
      .post<Validate2faEmailVerificationCodeResponse>(
        apiUrl,
        {
          token: flowData.form.verificationCode,
          forceSetEmailMfa: flowType === AccountSecurityFlowType.EnableEmailMfa,
        } as Validate2faEmailVerificationCodeRequest,
        httpOptions
      )
      .pipe(withLatestFrom(this.profile$))
      .subscribe({
        next: ([response, memberProfile]) => {
          const newProfile: Profile = {
            ...memberProfile,
            email: response.memberEmailChangedTo || memberProfile.email,
            emailVerified: true,
          };
          this.store$.dispatch(LoginActions.setProfile({ profile: newProfile }));

          this.store$.dispatch(
            LoginActions.setAccountSecurityFlowAction({
              flowType,
              flowAction: {
                name: AccountSecurityActionName.Validate2faEmailVerificationCode,
                isLoading: false,
                successData: response,
              },
            })
          );
        },
        error: (error: unknown) => {
          const errorResponseData = mapError<Validate2faEmailVerificationCodeErrorResponse>(error);

          const accountSecurityError: AccountSecurityError =
            isValidate2faEmailVerificationCodeErrorResponseWithCode(errorResponseData) &&
            errorResponseData.error.code === Validate2faEmailVerificationCodeErrorCode.INVALID_OR_EXPIRED_TOKEN
              ? AccountSecurityError.Validate2faEmailVerificationCodeInvalidOrExpiredCode
              : AccountSecurityError.Unknown;

          this.store$.dispatch(
            LoginActions.setAccountSecurityFlowAction({
              flowType,
              flowAction: {
                name: AccountSecurityActionName.Validate2faEmailVerificationCode,
                isLoading: false,
                error: accountSecurityError,
              },
            })
          );

          const errorMessage =
            flowType === AccountSecurityFlowType.ChangeEmail
              ? 'failed to change email'
              : flowType === AccountSecurityFlowType.VerifyEmail
                ? 'failed to verify email'
                : flowType === AccountSecurityFlowType.EnableEmailMfa
                  ? 'failed to verify and enable email MFA'
                  : 'failed to soft enforce email MFA';

          this.monitoringLogger.error(`Error in the process of validating the verification code - ${errorMessage}`, {
            error: mapErrorForMonitoring(error),
          });
        },
      });
  }

  private gAuthRequest<ResponseType>(path: string): Observable<ResponseType> {
    const token = snapshot(this.store$.pipe(loginToken()));
    return this.profile$.pipe(
      take(1),
      switchMap(({ memberNumber }) => {
        const httpOptions = {
          headers: new HttpHeaders({
            oauth_token: token,
            'x-api-key': this.configService.cfg.loyaltyServices.memberService.gauthApiKey,
          }),
        };
        return this.http.get<ResponseType>(
          `${this.configService.cfg.loyaltyServices.memberService.gauthUrl}/${path}/${memberNumber}?_=${Date.now()}`,
          httpOptions
        );
      }),
      catchError((error: unknown) => {
        this.monitoringLogger.warn('Error submitting GAuth request', { error: mapErrorForMonitoring(error) });
        return throwError(() => error);
      })
    );
  }

  private getLoyaltyServicesHttpHeaders(): HttpHeaders {
    const token = snapshot(this.store$.pipe(loginToken()));
    const apiKey = this.configService.cfg.loyaltyServices.legacy.apiKey;

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'x-api-key': apiKey,
    });
  }

  private getQRCodeImageUrl(secretKey: string): string {
    const { memberNumber } = snapshot(this.store$.pipe(profile()));

    return `https://${this.configService.cfg.casHost}/cas/gauth/qrgen?key=otpauth://totp/www.finnair.com${
      this.configService.cfg.casEnv === 'preprod' ? '(preprod)' : ''
    }:${memberNumber}?secret=${secretKey}&issuer=Finnair`;
  }
}
